import { useToast } from '@color/continuum';
import { ApiClient, ApiError } from '@color/lib';
import { useOnQueryError } from '@color/lib/QueryUtils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReferenceGenome, type TrackLoad, type TrackType } from 'igv';

import { getRequestErrorToastMessage } from 'ui/util/getRequestErrorToastMessage';

import {
  SAMPLE_SEQUENCE_RUN_IGV_ALIGNMENT_API_URL,
  SAMPLE_SEQUENCE_RUN_IGV_STATUS_API_URL,
  SAMPLE_SEQUENCE_RUN_REQUEST_ALIGNMENT_RESTORE_API_URL,
} from './paths';

const IGV_CONFIG_URL = '/api/v1/igv_config';

export type IGVConfig = ReferenceGenome & { tracks: TrackLoad<TrackType>[] };

export type IgvAlignmentStatus = 'available' | 'archived' | 'restoring' | 'unavailable';

export interface IgvAlignmentStatusResponse {
  status: IgvAlignmentStatus;
}

/**
 * Fetch the IGV reference genome config (genome + base tracks). This renders the
 * reference immediately, independent of any sample sequence run's reads.
 */
export const useIgvConfigQuery = () =>
  useQuery<IGVConfig, ApiError>({
    queryKey: [IGV_CONFIG_URL],
    queryFn: (): Promise<IGVConfig> => ApiClient.get(IGV_CONFIG_URL),
  });

/**
 * Fetch a sample sequence run's alignment (reads) track. Only enabled once the
 * run's alignment file is known to be available, since an archived/unavailable
 * file would otherwise fail to load.
 */
export const useSampleSequenceRunIgvAlignmentQuery = (
  ssrId: string | undefined,
  shouldLoadAlignment: boolean
) => {
  const alignmentUrl = ssrId ? SAMPLE_SEQUENCE_RUN_IGV_ALIGNMENT_API_URL(ssrId) : null;
  return useQuery<TrackLoad<'alignment'>, ApiError>({
    queryKey: [alignmentUrl],
    queryFn: (): Promise<TrackLoad<'alignment'>> => ApiClient.get(alignmentUrl as string),
    enabled: Boolean(alignmentUrl) && shouldLoadAlignment,
  });
};

/**
 * Fetch the archival status of a sample sequence run's IGV alignment file.
 *
 * The status lives on the sample sequence run (not the variant confirmation)
 * because it reflects the state of a single S3 object shared by everything that
 * renders that run's reads. Pass `undefined` to disable the query (e.g. when no
 * run is associated).
 */
export const useSampleSequenceRunIgvStatusQuery = (ssrId: string | undefined) => {
  const { showErrorToast } = useToast();
  const query = useQuery<IgvAlignmentStatusResponse, ApiError>({
    queryKey: [SAMPLE_SEQUENCE_RUN_IGV_STATUS_API_URL(ssrId ?? '')],
    queryFn: (): Promise<IgvAlignmentStatusResponse> =>
      ApiClient.get(SAMPLE_SEQUENCE_RUN_IGV_STATUS_API_URL(ssrId as string)),
    enabled: ssrId != null,
  });
  useOnQueryError(query, (error) => {
    // A missing run is surfaced contextually as a banner, so skip the toast.
    if (!error || error.status === 404) {
      return;
    }
    showErrorToast(`Error checking IGV alignment: ${getRequestErrorToastMessage(error)}`);
  });
  return query;
};

/**
 * Trigger a Glacier restore (unarchive) of a sample sequence run's alignment
 * file. Restricted to lab staff by the backend. The endpoint returns no body
 * (Glacier restores take hours), so we invalidate the status query to re-read
 * the true status rather than assuming it transitioned to `restoring`.
 */
export const useRequestSampleSequenceRunAlignmentRestoreMutation = (ssrId: string | undefined) => {
  const queryClient = useQueryClient();
  const { showErrorToast } = useToast();
  return useMutation<void, ApiError, void>({
    mutationFn: () =>
      ApiClient.post(SAMPLE_SEQUENCE_RUN_REQUEST_ALIGNMENT_RESTORE_API_URL(ssrId as string), {}),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SAMPLE_SEQUENCE_RUN_IGV_STATUS_API_URL(ssrId ?? '')],
      });
    },
    onError: (error) => {
      showErrorToast(getRequestErrorToastMessage(error));
    },
  });
};
